// filterControls.js

import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import MenuPages from "./MenuPages";
import { useNavigate } from "react-router-dom";

const normalizeString = (s) =>
  s ? s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase().trim().replace(/\s+/g, " ") : "";

const MARKER_IMG = "/favicon-16x16.png";

const INPUT_STYLE = {
  width: "100%",
  boxSizing: "border-box",
  height: 26,
  fontSize: 13,
  padding: "0 4px",
  border: "1px solid #767676",
  borderRadius: 3,
  background: "#fff",
};

const LIST_STYLE = {
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  margin: 0,
  padding: 0,
  listStyle: "none",
  border: "1px solid #767676",
  background: "#fff",
  zIndex: 9999,
  maxHeight: 220,
  overflowY: "auto",
  boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
};

// Componente genérico com busca e autocomplete
function SearchableSelect({ value, onChange, options, renderOption, renderValue }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOpen = () => {
    setOpen(true);
    setSearch("");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleSelect = useCallback((opt) => {
    onChange(opt);
    setOpen(false);
    setSearch("");
  }, [onChange]);

  const handleKeyDown = (e) => {
    if (e.key === "Escape") { setOpen(false); setSearch(""); }
    if (e.key === "Enter") {
      const match = filtered[0];
      if (match) handleSelect(match);
    }
  };

  const filtered = options.filter((opt) =>
    opt === "Todos" || normalizeString(opt).includes(normalizeString(search))
  );

  return (
    <div ref={ref} style={{ position: "relative", userSelect: "none" }}>
      {/* Trigger / campo de busca */}
      <div
        style={{
          ...INPUT_STYLE,
          display: "flex",
          alignItems: "center",
          position: "relative",
          padding: 0,
          cursor: "default",
        }}
      >
        {open ? (
          <input
            ref={inputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Pesquisar..."
            style={{
              flex: 1,
              height: "100%",
              border: "none",
              outline: "none",
              padding: "0 20px 0 4px",
              fontSize: INPUT_STYLE.fontSize,
              background: "transparent",
              width: "100%",
            }}
          />
        ) : (
          <div
            onClick={handleOpen}
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              height: "100%",
              padding: "0 20px 0 4px",
              overflow: "hidden",
            }}
          >
            {renderValue ? renderValue(value) : (
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {value}
              </span>
            )}
          </div>
        )}
        <span
          onClick={open ? () => { setOpen(false); setSearch(""); } : handleOpen}
          style={{
            position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)",
            fontSize: 9, color: "#555", cursor: "default", pointerEvents: "all",
          }}
        >
          {open ? "▲" : "▼"}
        </span>
      </div>

      {/* Lista de opções filtradas */}
      {open && (
        <ul style={LIST_STYLE}>
          {filtered.length === 0 ? (
            <li style={{ padding: "3px 6px", color: "#999", fontSize: INPUT_STYLE.fontSize }}>
              Nenhum resultado
            </li>
          ) : (
            filtered.map((opt) => {
              const selected = opt === value;
              return (
                <li
                  key={opt}
                  onClick={() => handleSelect(opt)}
                  style={{
                    padding: "3px 6px",
                    cursor: "default",
                    display: "flex",
                    alignItems: "center",
                    background: selected ? "#0078d7" : "transparent",
                    color: selected ? "#fff" : "#000",
                    fontSize: INPUT_STYLE.fontSize,
                  }}
                  onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = "#cde"; }}
                  onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = "transparent"; }}
                >
                  {renderOption ? renderOption(opt, selected) : (
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {opt}
                    </span>
                  )}
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}

const FilterControls = ({
  onDownloadNucleoCompleto,
  username,
  userRole,
  handleLogout,
  selectedTerritorio,
  setSelectedTerritorio,
  territoriosOptions,
  selectedMunicipio,
  setSelectedMunicipio,
  municipiosFiltrados,
  municipiosComNucleos,
  selectedNucleo,
  setSelectedNucleo,
  nucleoGidMap,
  layersVisibility,
  setLayersVisibility,
  lotes,
  quadras,
  nucleos,
  nucleosExternos,
  activeView,
  municipios,
  territorios
}) => {
  const [deleting, setDeleting] = useState(false);

  const toggleLayer = (layer) => {
    setLayersVisibility((prev) => ({ ...prev, [layer]: !prev[layer] }));
  };

  const navigate = useNavigate();

  const handleDeleteNucleo = async () => {
    const campo = activeView === 'painel1' ? 'NUCLEO' : 'NUCLEO_CASALEGAL_PAINEIS';
    if (!window.confirm(`Excluir o núcleo "${selectedNucleo}" e todos os seus lotes/quadras? Esta ação não pode ser desfeita.`)) return;
    
    setDeleting(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL;
      const API_KEY = process.env.REACT_APP_API_KEY;
      
      const response = await axios.delete(`${API_URL}/dados/nucleos/by-nome`, {
        headers: { Authorization: `Bearer ${API_KEY}` },
        data: { nome: selectedNucleo, campo },
      });

      alert(response.data.message || "Núcleo excluído com sucesso.");
      setSelectedNucleo("Todos");
      
      // Força o recarregamento ignorando o cache do navegador
      window.location.reload();
    } catch (err) {
      console.error("Erro ao excluir núcleo:", err);
      alert(err.response?.data?.message || "Erro ao excluir núcleo.");
    } finally {
      setDeleting(false);
    }
  };

  const layerOptions = [
    { key: "lotes", label: "Lotes", hasData: lotes?.length > 0 },
    { key: "quadras", label: "Quadras", hasData: quadras?.length > 0 },
    { key: "nucleos", label: "Núcleos", hasData: nucleos?.length > 0 },
    { key: "municipios", label: "Municípios", hasData: municipios?.length > 0 },
    { key: "territorios", label: "Territórios", hasData: territorios?.length > 0 },
  ];

  const municipiosOptions = municipiosFiltrados.filter(
    m => m === "Todos" || municipiosComNucleos?.has(m)
  );

  let nucleoOptionsList = [];
  if (activeView === 'painel2') {
    const normMun = normalizeString(selectedMunicipio);
    const nucleoNames = nucleosExternos
      .filter(nuc => selectedMunicipio === "Todos" || nuc.cidade_normalizada.includes(normMun))
      .map(nuc => nuc.nome);
    nucleoOptionsList = ["Todos", ...[...new Set(nucleoNames)].sort()];
  } else {
    nucleoOptionsList = nucleos;
  }

  // Render customizado para município: nome + ícone marcador à direita
  const renderMunicipioOption = (mun, selected) => {
    const temNucleo = mun !== "Todos" && municipiosComNucleos?.has(mun);
    return (
      <>
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {mun}
        </span>
        {temNucleo && (
          <img src={MARKER_IMG} alt="" style={{ width: 16, height: 16, marginLeft: 4, flexShrink: 0, opacity: selected ? 0.9 : 1 }} />
        )}
      </>
    );
  };

  const renderMunicipioValue = (mun) => (
    <>
      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {mun}
      </span>
      {municipiosComNucleos?.has(mun) && (
        <img src={MARKER_IMG} alt="" style={{ display: "none", width: 16, height: 16, marginLeft: 4, marginRight: 12, flexShrink: 0 }} />
      )}
    </>
  );

  return (
    <div style={{
      background: "#fff",
      padding: 20,
      gap: 30,
      borderRadius: 8,
      boxShadow: "0 2px 6px rgba(0,0,0,0.1)"
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <MenuPages navigate={navigate} />
        <button
          onClick={handleLogout}
          title="Sair"
          style={{
            padding: "4px 10px",
            background: "#c74848",
            color: "#fff",
            border: "none",
            borderRadius: 3,
            cursor: "pointer",
            fontSize: "12px",
            whiteSpace: "nowrap",
          }}
        >
          Sair
        </button>
      </div>
      <div className="central-label">
        NÚCLEO DE INFORMAÇÕES GE🌍ESPACIAIS - SPI
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "8px 16px", margin: "0px", alignItems: "end" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>

          <label>Território:</label>
          <SearchableSelect
            value={selectedTerritorio}
            onChange={(val) => {
              setSelectedTerritorio(val);
              setSelectedMunicipio("Todos");
              setSelectedNucleo("Todos");
            }}
            options={territoriosOptions || []}
          />

          <label>Município:</label>
          <SearchableSelect
            value={selectedMunicipio}
            onChange={(mun) => {
              setSelectedMunicipio(mun);
              setSelectedNucleo("Todos");
            }}
            options={municipiosOptions}
            renderOption={renderMunicipioOption}
            renderValue={renderMunicipioValue}
          />

          <label>Núcleo:</label>
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <div style={{ flex: 1 }}>
              <SearchableSelect
                value={selectedNucleo}
                onChange={setSelectedNucleo}
                options={nucleoOptionsList}
              />
            </div>
            {userRole === "admin" && selectedNucleo !== "Todos" && nucleoGidMap?.[selectedNucleo] && (
              <button
                title="Editar informações do núcleo"
                onClick={() => navigate(`/nucleo-editar/${nucleoGidMap[selectedNucleo]}`)}
                style={{
                  flexShrink: 0, height: 26, padding: "0 8px",
                  background: "#1976d2", color: "#fff",
                  border: "none", borderRadius: 3, cursor: "pointer",
                  fontSize: 13, display: "flex", alignItems: "center",
                }}
              >
                ✏
              </button>
            )}
            {userRole === "admin" && selectedNucleo !== "Todos" && (
              <button
                title="Excluir núcleo e seus registros"
                onClick={handleDeleteNucleo}
                disabled={deleting}
                style={{
                  flexShrink: 0, height: 26, padding: "0 8px",
                  background: deleting ? "#aaa" : "#c62828", color: "#fff",
                  border: "none", borderRadius: 3, cursor: deleting ? "default" : "pointer",
                  fontSize: 13, display: "flex", alignItems: "center",
                }}
              >
                🗑
              </button>
            )}
          </div>

          {(selectedTerritorio !== "Todos" || selectedMunicipio !== "Todos" || selectedNucleo !== "Todos") && (
            <button
              onClick={() => {
                setSelectedTerritorio("Todos");
                setSelectedMunicipio("Todos");
                setSelectedNucleo("Todos");
              }}
              style={{
                background: "none",
                border: "none",
                color: "#aaa",
                fontSize: "11px",
                cursor: "pointer",
                padding: "2px 0",
                textDecoration: "underline",
                textAlign: "left",
              }}
            >
              limpar filtros
            </button>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingBottom: 2 }}>

          <label style={{ fontWeight: "bold", fontSize: "11px", whiteSpace: "nowrap" }}>Exibir camadas:</label>
          {layerOptions.map(({ key, label, hasData }) =>
            hasData ? (
              <label key={key} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: "11px", whiteSpace: "nowrap" }}>
                <input
                  type="checkbox"
                  style={{ width: 11, height: 11, margin: 0 }}
                  checked={layersVisibility[key]}
                  onChange={() => toggleLayer(key)}
                />
                {label}
              </label>
            ) : null
          )}
          {userRole === "admin" && (
            <button
              onClick={() => navigate("/enviar-shape")}
              style={{
                padding: "4px 8px",
                background: "#2e7d32",
                color: "#fff",
                border: "none",
                borderRadius: 3,
                cursor: "pointer",
                fontSize: "11px",
                whiteSpace: "nowrap",
              }}
            >
              📤 Enviar Shape
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

export default FilterControls;
